/**
 * Libellés français du back-office.
 *
 * L'API ne transporte AUCUNE chaîne d'affichage (docs/06-revue-critique.md) : elle ne renvoie
 * que des énumérés, des horodatages ISO 8601 UTC et des valeurs numériques. Toute la traduction
 * vit donc ici, dans un seul fichier, exhaustif par construction : chaque table est typée
 * `Record<Enum, string>`, si bien qu'une valeur ajoutée au contrat partagé casse la compilation
 * du back-office au lieu d'afficher un code brut à un opérateur.
 *
 * Le back-office est monolingue français : c'est un outil interne. L'application mobile, elle,
 * est trilingue et traduit ces mêmes énumérés via i18next.
 */

import {
  ActeurAudit,
  CauseEchecCommande,
  Couleur,
  CriticiteAlerte,
  EtatAlimentation,
  EtatAppareil,
  EtatConnectivite,
  ModeleAppareil,
  NatureAlerte,
  PeriodeHistorique,
  RoleInterne,
  RoleUtilisateur,
  SourceMesure,
  StatutAlerte,
  StatutCommande,
  StatutExploitation,
  StatutInvitation,
  StatutMembre,
  StatutStockAppareil,
  StatutSynchronisation,
  StatutUtilisateur,
  TypeAlerte,
  TypeCommande,
  TypeEvenementObjet,
  TypeMessageMontant,
} from '@genviz/shared/enums';
import type { CodeErreur } from '@genviz/shared/errors';

/* -------------------------------------------------------------------------- */
/* Appareils                                                                   */
/* -------------------------------------------------------------------------- */

export const LIBELLE_ETAT_APPAREIL: Record<EtatAppareil, string> = {
  [EtatAppareil.NOMINAL]: 'Nominal',
  [EtatAppareil.ATTENTION]: 'Attention',
  [EtatAppareil.CRITIQUE]: 'Critique',
  [EtatAppareil.ETEINT]: 'Éteint',
};

export const LIBELLE_COULEUR: Record<Couleur, string> = {
  [Couleur.VERT]: 'Vert',
  [Couleur.ORANGE]: 'Orange',
  [Couleur.ROUGE]: 'Rouge',
  [Couleur.BLEU]: 'Bleu',
  [Couleur.GRIS]: 'Gris',
  [Couleur.NOIR]: 'Noir',
};

export const LIBELLE_ALIMENTATION: Record<EtatAlimentation, string> = {
  [EtatAlimentation.ON]: 'Sous tension',
  [EtatAlimentation.OFF]: 'Hors tension',
  [EtatAlimentation.INCONNU]: 'Inconnue',
};

export const LIBELLE_CONNECTIVITE: Record<EtatConnectivite, string> = {
  [EtatConnectivite.EN_LIGNE]: 'En ligne',
  [EtatConnectivite.HORS_LIGNE]: 'Hors ligne',
};

export const LIBELLE_MODELE: Record<ModeleAppareil, string> = {
  [ModeleAppareil.PORTABLE_SOLAIRE]: 'Portable solaire',
  [ModeleAppareil.SECTEUR]: 'Secteur',
  [ModeleAppareil.BATTERIE_12V]: 'Batterie 12 V',
};

export const LIBELLE_STATUT_STOCK: Record<StatutStockAppareil, string> = {
  [StatutStockAppareil.EN_STOCK]: 'En stock',
  [StatutStockAppareil.ENREGISTRE]: 'Appairé',
  [StatutStockAppareil.RETIRE]: 'Retiré',
  [StatutStockAppareil.SAV]: 'En SAV',
  [StatutStockAppareil.HORS_SERVICE]: 'Hors service',
};

/** Explication du statut de stock, affichée en aide au survol dans le tableau. */
export const AIDE_STATUT_STOCK: Record<StatutStockAppareil, string> = {
  [StatutStockAppareil.EN_STOCK]:
    'Provisionné en usine, jamais appairé. Son QR code est valide et l’appareil peut être vendu.',
  [StatutStockAppareil.ENREGISTRE]:
    'Appairé à une exploitation. Le secret d’appairage est consommé.',
  [StatutStockAppareil.RETIRE]:
    'Retiré de son exploitation par l’exploitant. Réappairable après régénération du secret.',
  [StatutStockAppareil.SAV]: 'Immobilisé au service après-vente. Ne publie plus sur le broker.',
  [StatutStockAppareil.HORS_SERVICE]:
    'Fin de vie : certificat révoqué, l’appareil ne peut plus se connecter.',
};

export const LIBELLE_STATUT_SYNC: Record<StatutSynchronisation, string> = {
  [StatutSynchronisation.SYNCHRONISE]: 'Synchronisé',
  [StatutSynchronisation.EN_ATTENTE]: 'En attente',
  [StatutSynchronisation.DIVERGENT]: 'Divergent',
};

/* -------------------------------------------------------------------------- */
/* Alertes et évènements                                                       */
/* -------------------------------------------------------------------------- */

export const LIBELLE_TYPE_ALERTE: Record<TypeAlerte, string> = {
  [TypeAlerte.NOMINAL]: 'Fonctionnement nominal',
  [TypeAlerte.TENSION_TEMPORAIREMENT_BASSE]: 'Tension temporairement basse',
  [TypeAlerte.BATTERIE_ENTRE_50_20]: 'Batterie entre 50 % et 20 %',
  [TypeAlerte.ABSENCE_TEMPORAIRE_COMMUNICATION]: 'Absence temporaire de communication',
  [TypeAlerte.TENSION_TROP_BASSE]: 'Tension trop basse',
  [TypeAlerte.ELECTRIFICATEUR_EN_MOUVEMENT]: 'Électrificateur en mouvement',
  [TypeAlerte.BATTERIE_SOUS_20]: 'Batterie sous 20 %',
  [TypeAlerte.BATTERIE_SECOURS_SOUS_SEUIL]: 'Batterie interne sous le seuil',
  [TypeAlerte.PERTE_COMMUNICATION]: 'Perte de communication',
  [TypeAlerte.ELECTRIFICATEUR_ETEINT]: 'Électrificateur éteint',
  [TypeAlerte.ELECTRIFICATEUR_MIS_EN_ROUTE]: 'Électrificateur mis en route',
  [TypeAlerte.ARRET_MOUVEMENT]: 'Arrêt du mouvement',
  [TypeAlerte.RETOUR_FONCTIONNEMENT_NORMAL]: 'Retour au fonctionnement normal',
  [TypeAlerte.SEUIL_GARDIENNAGE_MODIFIE]: 'Modification de la tension de gardiennage',
};

export const LIBELLE_NATURE_ALERTE: Record<NatureAlerte, string> = {
  [NatureAlerte.ALERTE]: 'Alerte',
  [NatureAlerte.EVENEMENT]: 'Évènement',
};

export const LIBELLE_CRITICITE: Record<CriticiteAlerte, string> = {
  [CriticiteAlerte.NOMINAL]: 'Nominal',
  [CriticiteAlerte.ATTENTION]: 'Attention',
  [CriticiteAlerte.CRITIQUE]: 'Critique',
  [CriticiteAlerte.EVENEMENT]: 'Évènement',
};

export const LIBELLE_STATUT_ALERTE: Record<StatutAlerte, string> = {
  [StatutAlerte.ACTIVE]: 'Active',
  [StatutAlerte.RESOLUE]: 'Résolue',
};

/* -------------------------------------------------------------------------- */
/* Commandes                                                                   */
/* -------------------------------------------------------------------------- */

export const LIBELLE_TYPE_COMMANDE: Record<TypeCommande, string> = {
  [TypeCommande.POWER_ON]: 'Mise sous tension',
  [TypeCommande.POWER_OFF]: 'Mise hors tension',
  [TypeCommande.START_MEASURE_SESSION]: 'Session de mesure',
  [TypeCommande.SET_GUARD_VOLTAGE]: 'Tension de gardiennage',
  [TypeCommande.SET_GEOLOC_FREQ]: 'Fréquence de géolocalisation',
  [TypeCommande.SET_MOVEMENT_WATCH]: 'Surveillance de mouvement',
  [TypeCommande.SET_HEARTBEAT_PERIOD]: 'Cadence de heartbeat',
  [TypeCommande.REBOOT]: 'Redémarrage du module',
  [TypeCommande.OTA_UPDATE]: 'Mise à jour firmware',
  [TypeCommande.UNBIND]: 'Désassociation',
};

export const LIBELLE_STATUT_COMMANDE: Record<StatutCommande, string> = {
  [StatutCommande.EN_ATTENTE]: 'En attente',
  [StatutCommande.ENVOYEE]: 'Envoyée',
  [StatutCommande.ACQUITTEE]: 'Acquittée',
  [StatutCommande.APPLIQUEE]: 'Appliquée',
  [StatutCommande.ECHOUEE]: 'Échouée',
  [StatutCommande.EXPIREE]: 'Expirée',
  [StatutCommande.ANNULEE]: 'Annulée',
};

export const LIBELLE_CAUSE_ECHEC: Record<CauseEchecCommande, string> = {
  [CauseEchecCommande.APPAREIL_HORS_LIGNE]: 'Appareil hors ligne',
  [CauseEchecCommande.REFUS_APPAREIL]: 'Refusée par l’appareil',
  [CauseEchecCommande.DELAI_DEPASSE]: 'Délai dépassé',
  [CauseEchecCommande.NON_SUPPORTEE]: 'Commande non supportée',
};

/* -------------------------------------------------------------------------- */
/* Trames et télémétrie                                                        */
/* -------------------------------------------------------------------------- */

export const LIBELLE_TYPE_MESSAGE: Record<TypeMessageMontant, string> = {
  [TypeMessageMontant.HEARTBEAT]: 'Heartbeat',
  [TypeMessageMontant.MEASURE]: 'Mesure',
  [TypeMessageMontant.POSITION]: 'Position',
  [TypeMessageMontant.EVENT]: 'Évènement',
  [TypeMessageMontant.ACK]: 'Acquittement',
  [TypeMessageMontant.BOOT]: 'Démarrage',
  [TypeMessageMontant.DIAG]: 'Diagnostic',
  [TypeMessageMontant.STATE]: 'État (LWT)',
};

export const LIBELLE_EVENEMENT_OBJET: Record<TypeEvenementObjet, string> = {
  [TypeEvenementObjet.POWER_ON]: 'Mise sous tension',
  [TypeEvenementObjet.POWER_OFF]: 'Mise hors tension',
  [TypeEvenementObjet.MOVEMENT_START]: 'Début de mouvement',
  [TypeEvenementObjet.MOVEMENT_END]: 'Fin de mouvement',
  [TypeEvenementObjet.LOW_BATTERY]: 'Batterie faible',
  [TypeEvenementObjet.BACKUP_LOW_BATTERY]: 'Batterie interne faible',
  [TypeEvenementObjet.TAMPER]: 'Ouverture du boîtier',
};

export const LIBELLE_SOURCE_MESURE: Record<SourceMesure, string> = {
  [SourceMesure.PERIODIQUE]: 'Périodique',
  [SourceMesure.SESSION_TEMPS_REEL]: 'Session temps réel',
};

export const LIBELLE_PERIODE: Record<PeriodeHistorique, string> = {
  [PeriodeHistorique.H2]: '2 h',
  [PeriodeHistorique.H24]: '24 h',
  [PeriodeHistorique.J7]: '7 j',
};

/* -------------------------------------------------------------------------- */
/* Comptes, rôles, exploitations                                               */
/* -------------------------------------------------------------------------- */

export const LIBELLE_ROLE_INTERNE: Record<RoleInterne, string> = {
  [RoleInterne.STAFF_SUPPORT]: 'Support / SAV',
  [RoleInterne.STAFF_OPERATEUR]: 'Opérateur usine',
  [RoleInterne.STAFF_ADMIN]: 'Administrateur back-office',
};

export const LIBELLE_ROLE_UTILISATEUR: Record<RoleUtilisateur, string> = {
  [RoleUtilisateur.ADMIN]: 'Administrateur',
  [RoleUtilisateur.USER]: 'Utilisateur',
};

export const LIBELLE_STATUT_UTILISATEUR: Record<StatutUtilisateur, string> = {
  [StatutUtilisateur.ACTIF]: 'Actif',
  [StatutUtilisateur.DESACTIVE]: 'Désactivé',
  [StatutUtilisateur.RETIRE]: 'Retiré',
};

export const LIBELLE_STATUT_MEMBRE: Record<StatutMembre, string> = {
  [StatutMembre.ACTIF]: 'Actif',
  [StatutMembre.INVITATION_EN_ATTENTE]: 'Invitation en attente',
  [StatutMembre.DESACTIVE]: 'Désactivé',
  [StatutMembre.RETIRE]: 'Retiré',
};

export const LIBELLE_STATUT_EXPLOITATION: Record<StatutExploitation, string> = {
  [StatutExploitation.ACTIVE]: 'Active',
  [StatutExploitation.SUSPENDUE]: 'Suspendue',
  [StatutExploitation.SUPPRIMEE]: 'Supprimée',
};

export const LIBELLE_STATUT_INVITATION: Record<StatutInvitation, string> = {
  [StatutInvitation.EN_ATTENTE]: 'En attente',
  [StatutInvitation.ACCEPTEE]: 'Acceptée',
  [StatutInvitation.EXPIREE]: 'Expirée',
  [StatutInvitation.REVOQUEE]: 'Révoquée',
};

/* -------------------------------------------------------------------------- */
/* Journal d'audit                                                             */
/* -------------------------------------------------------------------------- */

export const LIBELLE_ACTEUR_AUDIT: Record<ActeurAudit, string> = {
  [ActeurAudit.UTILISATEUR]: 'Utilisateur',
  [ActeurAudit.OBJET]: 'Objet',
  [ActeurAudit.BACKOFFICE]: 'Back-office',
  [ActeurAudit.SYSTEME]: 'Système',
};

/**
 * Actions d'audit connues. La liste des codes d'action n'est pas figée par le contrat :
 * un code inconnu est affiché tel quel plutôt que masqué (`libelleAction`).
 */
export const LIBELLE_ACTION_AUDIT: Record<string, string> = {
  LOGIN: 'Connexion',
  LOGOUT: 'Déconnexion',
  LOGIN_FAILED: 'Échec de connexion',
  PASSWORD_CHANGED: 'Changement de mot de passe',
  PASSWORD_RESET: 'Réinitialisation de mot de passe',
  EXPLOITATION_CREATED: 'Création d’exploitation',
  EXPLOITATION_SUSPENDED: 'Suspension d’exploitation',
  EXPLOITATION_REACTIVATED: 'Réactivation d’exploitation',
  USER_INVITED: 'Invitation envoyée',
  USER_JOINED: 'Rattachement d’un utilisateur',
  USER_REMOVED: 'Retrait d’un utilisateur',
  USER_DISABLED: 'Désactivation de compte',
  USER_ENABLED: 'Réactivation de compte',
  ADMIN_TRANSFER_REQUESTED: 'Demande de transfert d’administrateur',
  ADMIN_TRANSFER_ACCEPTED: 'Transfert d’administrateur accepté',
  DEVICE_REGISTERED: 'Appairage d’un appareil',
  DEVICE_RENAMED: 'Renommage d’un appareil',
  DEVICE_REMOVED: 'Retrait d’un appareil',
  DEVICE_POWER_ON: 'Commande : mise sous tension',
  DEVICE_POWER_OFF: 'Commande : mise hors tension',
  DEVICE_SETTINGS_UPDATED: 'Modification du paramétrage',
  DEVICE_UNBOUND: 'Désassociation forcée',
  DEVICE_STATUS_CHANGED: 'Changement de statut de stock',
  STOCK_IMPORTED: 'Import de stock',
  ALERTS_PURGED: 'Purge des alertes',
  MOVEMENT_REFERENCE_RESET: 'Repositionnement du point de référence',
  FIRMWARE_CAMPAIGN_CREATED: 'Création d’une campagne firmware',
};

/** Libellé d'une action d'audit, avec repli sur le code brut quand il est inconnu. */
export function libelleAction(action: string): string {
  return LIBELLE_ACTION_AUDIT[action] ?? action;
}

/* -------------------------------------------------------------------------- */
/* Codes d'erreur                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Messages des codes d'erreur susceptibles d'atteindre un opérateur du back-office.
 * Le catalogue complet vit dans `@genviz/shared/errors` ; un code absent d'ici est affiché
 * sous sa forme brute, ce qui reste exploitable par le support (et signale la traduction
 * manquante) plutôt que d'afficher un message générique trompeur.
 */
export const LIBELLE_ERREUR: Partial<Record<CodeErreur, string>> = {
  NON_AUTHENTIFIE: 'Session absente ou expirée. Reconnectez-vous.',
  JETON_ACCES_INVALIDE: 'Session invalide. Reconnectez-vous.',
  JETON_ACCES_EXPIRE: 'Session expirée. Reconnectez-vous.',
  JETON_RAFRAICHISSEMENT_INVALIDE: 'Session expirée. Reconnectez-vous.',
  JETON_RAFRAICHISSEMENT_REJOUE:
    'Jeton de session rejoué : toutes les sessions ont été révoquées par sécurité.',
  IDENTIFIANTS_INVALIDES: 'Adresse e-mail ou mot de passe incorrect.',
  MOT_DE_PASSE_INCORRECT: 'Mot de passe incorrect.',
  ADRESSE_EMAIL_INCONNUE: 'Adresse e-mail inconnue.',
  COMPTE_DESACTIVE: 'Ce compte est désactivé.',
  COMPTE_VERROUILLE: 'Compte temporairement verrouillé après plusieurs échecs.',
  ROLE_NON_AUTORISE: 'Votre rôle ne permet pas cette opération.',
  AUCUNE_EXPLOITATION: 'Aucune exploitation rattachée à ce compte.',

  UTILISATEUR_INTROUVABLE: 'Utilisateur introuvable.',
  DERNIER_ADMINISTRATEUR:
    'Impossible : c’est le dernier administrateur de l’exploitation.',
  RETRAIT_DE_SOI_INTERDIT: 'Vous ne pouvez pas vous retirer vous-même.',

  APPAREIL_INTROUVABLE: 'Appareil introuvable dans le stock.',
  APPAREIL_INEXISTANT: 'Aucun appareil ne porte ce numéro de série.',
  APPAREIL_DEJA_ENREGISTRE: 'Cet appareil est déjà appairé à une exploitation.',
  APPAREIL_NON_ASSOCIE: 'Cet appareil n’est associé à aucune exploitation.',
  APPAREIL_HORS_LIGNE: 'Appareil hors ligne : la commande n’a pas pu être remise.',
  APPAREIL_ETEINT: 'Électrificateur éteint : il n’y a rien à mesurer.',
  APPAREIL_COMMANDES_EN_ATTENTE: 'Des commandes sont encore en vol sur cet appareil.',
  NUMERO_SERIE_INCORRECT: 'Numéro de série au format invalide.',
  NUMERO_SERIE_DUPLIQUE: 'Ce numéro de série existe déjà en stock.',
  CLE_APPAIRAGE_INVALIDE: 'Clé d’appairage invalide.',

  COMMANDE_INTROUVABLE: 'Commande introuvable.',
  COMMANDE_EXPIREE: 'Commande expirée.',
  COMMANDE_DEJA_TERMINEE: 'Commande déjà dans un état terminal.',
  COMMANDE_NON_SUPPORTEE: 'Commande non supportée par ce modèle.',

  ALERTE_INTROUVABLE: 'Alerte introuvable.',

  VALEUR_HORS_BORNES: 'Valeur hors des bornes autorisées.',
  VALEUR_NON_AUTORISEE: 'Valeur non autorisée.',
  FENETRE_NON_SUPPORTEE: 'Fenêtre temporelle non supportée.',

  CHAMP_OBLIGATOIRE: 'Champ obligatoire.',
  ERREUR_VALIDATION: 'Certaines valeurs sont invalides.',
  SCHEMA_INVALIDE: 'Format de données invalide.',
  CONFIRMATION_REQUISE: 'Confirmation explicite requise.',
  PRECONDITION_ECHOUEE: 'La ressource a changé entre-temps. Rechargez la page.',
  RESSOURCE_INTROUVABLE: 'Ressource introuvable.',
  TROP_DE_REQUETES: 'Trop de requêtes. Patientez quelques instants.',
  SERVICE_INDISPONIBLE: 'Service momentanément indisponible.',
  ERREUR_INTERNE: 'Erreur interne du serveur. Le support a été notifié.',
};

/* -------------------------------------------------------------------------- */
/* Libellés d'interface récurrents                                             */
/* -------------------------------------------------------------------------- */

/** Valeur affichée à la place d'une donnée absente. Jamais « 0 », jamais une chaîne vide. */
export const VALEUR_ABSENTE = '—';
